import axios from "axios";
import {
  fetchAndSaveSubscriptionsFromServer,
  getSubscribedProductNames,
  routeToProduct,
} from "./features/payment/SubscriptionInfo";
import {
  clearAuthSession,
  getAccessToken,
  isTokenExpired,
  refreshAccessToken,
} from "./utils/auth";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASEURL,
});

let refreshTokenPromise: Promise<string> | null = null;

const AUTH_ENDPOINTS = [
  "users/auth/token",
  "users/auth/token/refresh",
  "/users/auth/google",
];

const isAuthEndpoint = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

const redirectToLogin = () => {
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const getFreshAccessToken = async (): Promise<string> => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = refreshAccessToken().finally(() => {
      refreshTokenPromise = null;
    });
  }

  return refreshTokenPromise;
};

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    if (isAuthEndpoint(config.url)) {
      return config;
    }

    const token = getAccessToken();
    if (token && !isTokenExpired(token)) {
      config.headers["Authorization"] = "Bearer " + token;
      return config;
    }

    if (token && isTokenExpired(token)) {
      try {
        const newAccessToken = await getFreshAccessToken();
        config.headers["Authorization"] = `Bearer ${newAccessToken}`;
      } catch (error) {
        clearAuthSession();
        redirectToLogin();
        return Promise.reject(error);
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      isAuthEndpoint(originalRequest?.url)
    ) {
      return Promise.reject(error);
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await getFreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosInstance.request(originalRequest);
      } catch (refreshError) {
        clearAuthSession();
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    if (error.response && error.response.status === 403) {
      try {
        const requiredProduct = routeToProduct[window.location.pathname];
        if (requiredProduct) {
          let subscribedProducts = getSubscribedProductNames();

          if (!subscribedProducts || subscribedProducts.length === 0) {
            try {
              subscribedProducts = await fetchAndSaveSubscriptionsFromServer();
            } catch (err) {
              subscribedProducts = [];
            }
          }

          if (!subscribedProducts.includes(requiredProduct)) {
            // encode message and include the frontend path so Plans can show context
            const msg = encodeURIComponent(
              `subscription_required:${window.location.pathname || "/"}`,
            );
            window.location.href = `/plans?reason=${msg}`;
            return Promise.reject(
              Object.assign(error, {
                message: "Forbidden - subscription required (403)",
              }),
            );
          }
        }
      } catch (err) {
        console.error("Error while handling 403 in interceptor:", err);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
